import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

export type DateMode = 'ytd' | 'year' | 'quarter' | 'month' | 'custom' | 'all';
export type GroupBy = 'day' | 'week' | 'month' | 'quarter' | 'year';

interface UseTabDateNavOptions {
  /**
   * URL-param key prefix. Each tab passes its own prefix (e.g. `f` for
   * Financials, `pm` for Program Metrics) when multiple per-tab navs
   * coexist in the same URL. Empty prefix produces unprefixed keys
   * (`mode`, `year`, etc.) for a single shared nav.
   */
  prefix: string;

  /**
   * Pathname guard for the URL-sync effect (per
   * `feedback_tab_url_state_guard`). When set, the effect skips its
   * write if the current pathname no longer matches — protects
   * against a late-running effect clobbering a different tab's URL
   * after a tab switch.
   *
   * Omit for a SHARED nav whose state is the same regardless of which
   * tab is active — there's no tab-leakage risk because only one nav
   * exists.
   */
  expectedPath?: string;

  /**
   * groupBy controls the granularity of `handleNavigate('prev'|'next')`
   * shifts in custom mode (day/week/month/quarter/year shift).
   */
  groupBy: GroupBy;
}

interface UseTabDateNavReturn {
  dateMode: DateMode;
  navYear: number;
  navQuarter: number;
  navMonth: number;
  customStart: string;
  customEnd: string;
  dateRange: { start_date: string; end_date: string };
  dateDisplay: string;

  switchMode: (mode: DateMode) => void;
  handleNavigate: (direction: 'prev' | 'next') => void;
  handleToday: () => void;

  // Date-picker modal state. Exposed so a host can render its picker
  // UI without re-implementing the buffered selection logic.
  showDatePicker: boolean;
  setShowDatePicker: (v: boolean) => void;
  pickerYear: number;
  setPickerYear: (v: number) => void;
  pickerQuarter: number;
  setPickerQuarter: (v: number) => void;
  pickerMonth: number;
  setPickerMonth: (v: number) => void;
  pickerStart: string;
  setPickerStart: (v: string) => void;
  pickerEnd: string;
  setPickerEnd: (v: string) => void;
  openDatePicker: () => void;
  applyDatePicker: () => void;
}

const lastDayOfMonth = (year: number, month: number): string => {
  const days = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(days).padStart(2, '0')}`;
};

// useTabDateNav — state + handlers + URL persistence for a date-range
// navigator. Designed for use inside a route-tab component: pass a
// prefix so multiple tabs can write to the same URL search string
// without collision, and pass `expectedPath` so the URL-sync effect
// skips when the active tab has changed.
export function useTabDateNav({ prefix, expectedPath, groupBy }: UseTabDateNavOptions): UseTabDateNavReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const currentQuarter = Math.ceil(month / 3);

  const k = useCallback((key: string) => (prefix ? `${prefix}_${key}` : key), [prefix]);

  // Initial state from URL params. State is owned by the hook so this
  // is a one-time hydration; subsequent URL writes come from the
  // effect at the bottom. Default is 'year' — most users want a
  // full-year overview when they land on a tab.
  const [dateMode, setDateMode] = useState<DateMode>((searchParams.get(k('mode')) as DateMode) || 'year');
  const [navYear, setNavYear] = useState(parseInt(searchParams.get(k('year')) || String(year)));
  const [navQuarter, setNavQuarter] = useState(parseInt(searchParams.get(k('quarter')) || String(currentQuarter)));
  const [navMonth, setNavMonth] = useState(parseInt(searchParams.get(k('month')) || String(month)));
  const [customStart, setCustomStart] = useState(searchParams.get(k('start')) || '');
  const [customEnd, setCustomEnd] = useState(searchParams.get(k('end')) || '');

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(year);
  const [pickerQuarter, setPickerQuarter] = useState(currentQuarter);
  const [pickerMonth, setPickerMonth] = useState(month);
  const [pickerStart, setPickerStart] = useState('');
  const [pickerEnd, setPickerEnd] = useState('');

  const dateRange = useMemo(() => {
    switch (dateMode) {
      case 'ytd':
        return { start_date: `${year}-01-01`, end_date: lastDayOfMonth(year, month) };
      case 'year':
        return { start_date: `${navYear}-01-01`, end_date: `${navYear}-12-31` };
      case 'quarter': {
        const qStartMonth = (navQuarter - 1) * 3 + 1;
        const qEndMonth = navQuarter * 3;
        return {
          start_date: `${navYear}-${String(qStartMonth).padStart(2, '0')}-01`,
          end_date: lastDayOfMonth(navYear, qEndMonth),
        };
      }
      case 'month':
        return {
          start_date: `${navYear}-${String(navMonth).padStart(2, '0')}-01`,
          end_date: lastDayOfMonth(navYear, navMonth),
        };
      case 'custom':
        return { start_date: customStart, end_date: customEnd };
      case 'all':
        return { start_date: '', end_date: '' };
    }
  }, [dateMode, year, month, navYear, navQuarter, navMonth, customStart, customEnd]);

  const dateDisplay = useMemo(() => {
    if (dateMode === 'all') return 'All time';
    if (dateMode === 'year') return `${navYear}`;
    if (dateMode === 'quarter') return `${navYear}-Q${navQuarter}`;
    if (dateMode === 'month') return `${navYear}-${String(navMonth).padStart(2, '0')}`;
    if (!dateRange.start_date || !dateRange.end_date) return 'Select dates';
    return `${dateRange.start_date} to ${dateRange.end_date}`;
  }, [dateMode, navYear, navQuarter, navMonth, dateRange]);

  const shiftDate = useCallback((dateStr: string, shift: number): string => {
    const [y, m, d] = dateStr.split('-').map(Number);
    let nd: Date;
    switch (groupBy) {
      case 'day': nd = new Date(y, m - 1, d + shift); break;
      case 'week': nd = new Date(y, m - 1, d + 7 * shift); break;
      case 'month': {
        const nm = m - 1 + shift;
        const last = new Date(y, nm + 1, 0).getDate();
        nd = new Date(y, nm, Math.min(d, last));
        break;
      }
      case 'quarter': {
        const nm = m - 1 + 3 * shift;
        const last = new Date(y, nm + 1, 0).getDate();
        nd = new Date(y, nm, Math.min(d, last));
        break;
      }
      case 'year': {
        const last = new Date(y + shift, m, 0).getDate();
        nd = new Date(y + shift, m - 1, Math.min(d, last));
        break;
      }
      default: nd = new Date(y, m - 1, d);
    }
    return `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}-${String(nd.getDate()).padStart(2, '0')}`;
  }, [groupBy]);

  const handleNavigate = useCallback((direction: 'prev' | 'next') => {
    const mult = direction === 'prev' ? -1 : 1;
    if (dateMode === 'year') {
      setNavYear(navYear + mult);
    } else if (dateMode === 'quarter') {
      let newQuarter = navQuarter + mult;
      let newYear = navYear;
      if (newQuarter > 4) { newQuarter = 1; newYear++; }
      if (newQuarter < 1) { newQuarter = 4; newYear--; }
      setNavQuarter(newQuarter);
      setNavYear(newYear);
    } else if (dateMode === 'month') {
      let newMonth = navMonth + mult;
      let newYear = navYear;
      if (newMonth > 12) { newMonth = 1; newYear++; }
      if (newMonth < 1) { newMonth = 12; newYear--; }
      setNavMonth(newMonth);
      setNavYear(newYear);
    } else if (dateMode === 'custom') {
      setCustomStart(shiftDate(dateRange.start_date, mult));
      setCustomEnd(shiftDate(dateRange.end_date, mult));
    }
  }, [dateMode, navYear, navQuarter, navMonth, dateRange, shiftDate]);

  const handleToday = useCallback(() => {
    if (dateMode === 'year') {
      setNavYear(year);
    } else if (dateMode === 'quarter') {
      setNavYear(year);
      setNavQuarter(currentQuarter);
    } else if (dateMode === 'month') {
      setNavYear(year);
      setNavMonth(month);
    } else if (dateMode === 'custom') {
      setCustomStart(`${year}-${String(month).padStart(2, '0')}-01`);
      setCustomEnd(lastDayOfMonth(year, month));
    }
  }, [dateMode, year, month, currentQuarter]);

  const switchMode = useCallback((mode: DateMode) => {
    if (mode === dateMode) return;
    if (mode === 'year') {
      const [sy] = (dateRange.start_date || '').split('-').map(Number);
      setNavYear(sy || year);
    } else if (mode === 'quarter') {
      const [sy, sm] = (dateRange.start_date || '').split('-').map(Number);
      setNavYear(sy || year);
      setNavQuarter(Math.ceil((sm || month) / 3));
    } else if (mode === 'month') {
      const parts = (dateRange.start_date || '').split('-').map(Number);
      setNavYear(parts[0] || year);
      setNavMonth(parts[1] || month);
    } else if (mode === 'custom') {
      const fallbackStart = `${year}-${String(month).padStart(2, '0')}-01`;
      const fallbackEnd = lastDayOfMonth(year, month);
      setCustomStart(dateRange.start_date || fallbackStart);
      setCustomEnd(dateRange.end_date || fallbackEnd);
    }
    setDateMode(mode);
  }, [dateMode, dateRange, year, month]);

  const openDatePicker = useCallback(() => {
    if (dateMode === 'year') {
      setPickerYear(navYear);
    } else if (dateMode === 'quarter') {
      setPickerYear(navYear);
      setPickerQuarter(navQuarter);
    } else if (dateMode === 'month') {
      setPickerYear(navYear);
      setPickerMonth(navMonth);
    } else {
      setPickerStart(dateRange.start_date);
      setPickerEnd(dateRange.end_date);
    }
    setShowDatePicker(true);
  }, [dateMode, navYear, navQuarter, navMonth, dateRange]);

  const applyDatePicker = useCallback(() => {
    if (dateMode === 'year') {
      setNavYear(pickerYear);
    } else if (dateMode === 'quarter') {
      setNavYear(pickerYear);
      setNavQuarter(pickerQuarter);
    } else if (dateMode === 'month') {
      setNavYear(pickerYear);
      setNavMonth(pickerMonth);
    } else if (dateMode === 'custom') {
      setCustomStart(pickerStart);
      setCustomEnd(pickerEnd);
    }
    setShowDatePicker(false);
  }, [dateMode, pickerYear, pickerQuarter, pickerMonth, pickerStart, pickerEnd]);

  // URL sync. The pathname guard (per feedback_tab_url_state_guard) is
  // critical when tabs unmount: a state update queued just before
  // switching tabs would otherwise commit AFTER the new tab has
  // already written its own URL, clobbering it.
  useEffect(() => {
    if (expectedPath !== undefined && location.pathname !== expectedPath) return;
    const params = new URLSearchParams(searchParams);
    params.set(k('mode'), dateMode);
    if (dateMode === 'ytd' || dateMode === 'all') {
      params.delete(k('year'));
      params.delete(k('quarter'));
      params.delete(k('month'));
      params.delete(k('start'));
      params.delete(k('end'));
    } else if (dateMode === 'year') {
      params.set(k('year'), String(navYear));
      params.delete(k('quarter'));
      params.delete(k('month'));
      params.delete(k('start'));
      params.delete(k('end'));
    } else if (dateMode === 'quarter') {
      params.set(k('year'), String(navYear));
      params.set(k('quarter'), String(navQuarter));
      params.delete(k('month'));
      params.delete(k('start'));
      params.delete(k('end'));
    } else if (dateMode === 'month') {
      params.set(k('year'), String(navYear));
      params.set(k('month'), String(navMonth));
      params.delete(k('quarter'));
      params.delete(k('start'));
      params.delete(k('end'));
    } else if (dateMode === 'custom') {
      if (customStart) params.set(k('start'), customStart); else params.delete(k('start'));
      if (customEnd) params.set(k('end'), customEnd); else params.delete(k('end'));
      params.delete(k('year'));
      params.delete(k('quarter'));
      params.delete(k('month'));
    }
    setSearchParams(params, { replace: true });
    // Intentionally NOT depending on searchParams — that would create
    // a feedback loop. The intent is "when local state changes,
    // reflect into URL", not "when URL changes, ...".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateMode, navYear, navQuarter, navMonth, customStart, customEnd, location.pathname, expectedPath, setSearchParams, k]);

  return {
    dateMode,
    navYear,
    navQuarter,
    navMonth,
    customStart,
    customEnd,
    dateRange,
    dateDisplay,
    switchMode,
    handleNavigate,
    handleToday,
    showDatePicker,
    setShowDatePicker,
    pickerYear,
    setPickerYear,
    pickerQuarter,
    setPickerQuarter,
    pickerMonth,
    setPickerMonth,
    pickerStart,
    setPickerStart,
    pickerEnd,
    setPickerEnd,
    openDatePicker,
    applyDatePicker,
  };
}
